<template>
    <div :data-t="dataT.WRAPPER"
         :class="className">
        <button v-if="showNav" :class="backwardClass" v-on:click="scrollBackward" title="Pokaż poprzednie" :data-t="dataT.BACKWARD">
            <icon-component name="angle-left" :width="14" :height="14" />
        </button>
        <ul ref="slider"
            :class="innerClassName"
            :data-t="dataT.SLIDER"
            v-on:scroll="onScroll">
            <slot name="items" />
        </ul>
        <button v-if="showNav" :class="forwardClass" v-on:click="scrollForward" title="Pokaż następne" :data-t="dataT.FORWARD">
            <icon-component name="angle-right" :width="14" :height="14" />
        </button>
        <nav v-if="visibleIndicators" :class="indicatorsClassName" ref="indicators">
            <span v-for="(item, index) in indicatorsNumber"
                  :class="{'active': isIndicatorActive(index)}"
                  :style="indicatorsStyle"
                  v-on:click.prevent="indicatorClick(index)"></span>
        </nav>
    </div>
</template>
<script>
    import IconComponent from './icon-component.vue';
    import throttle from '~/utils/throttle';
    import debounce from '~/utils/debounce';
    import { SIZES } from '../../utils/screen-sizes';

    const listenersConfig = {
        passive: true,
        capture: false
    };

    const DATA_T_ATTRIBUTES = {
        WRAPPER: 21.1,
        BACKWARD: 21.2,
        FORWARD: 21.3,
        SLIDER: 21.4
    };

    export default {
        name: 'dm-scrollable-list',
        components: {
            'icon-component': IconComponent,
        },
        data() {
            return {
                swipeActive: false,
                tileWidth: 0,
                indicatorsWidth: 0,
                indicatorsDisplacement: 0,
                scrollPosition: 0,
                windowWidth: window.innerWidth
            };
        },
        props: {
            dataT: {
                type: Object,
                default: () => DATA_T_ATTRIBUTES
            },
            className: {
                type: String,
                required: false
            },
            innerClassName: {
                type: String,
                required: false
            },
            navClassName: {
                type: String,
                required: false
            },
            itemsInViewport: {
                type: Number,
                required: true
            },
            mobileItemsInViewport: {
                type: Number,
                required: false
            },
            tabletItemsInViewport: {
                type: Number,
                required: false
            },
            visibleIndicators: {
                type: Number,
                default: 0
            },
            columnsTotal: {
                type: Number,
                required: false,
                default() {
                    return this.$slots.items.length;
                }
            }
        },
        computed: {
            scrollableLength() {
                return Math.round(this.tileWidth * (this.columnsTotal - this.itemsPerSlide));
            },
            showNav() {
                return this.columnsTotal > this.itemsPerSlide;
            },
            backwardClass() {
                return `${this.navClassName} ${this.navClassName}--backward`;
            },
            forwardClass() {
                return `${this.navClassName} ${this.navClassName}--forward`;
            },
            scrollToMethodAvailable() {
                return !!document.body.scrollTo;
            },
            indicatorsStyle() {
                return `transform: translateX(${this.indicatorsDisplacement}px);`;
            },
            indicatorsClassName() {
                const className = this.indicatorsNumber <= this.visibleIndicators ? '_bc' : '';
                return `${this.className}__indicators ${className}`;
            },
            indicatorsNumber() {
                return Math.ceil(this.columnsTotal / this.itemsPerSlide);
            },
            itemsPerSlide() {
                if (this.windowWidth < SIZES.MEDIUM) {
                    return this.mobileItemsInViewport || this.itemsInViewport;
                } else if (this.windowWidth < SIZES.LARGE) {
                    return this.tabletItemsInViewport || this.itemsInViewport;
                } else {
                    return this.itemsInViewport;
                }   
            }
        },
        methods: {
            async getFuturePosition() {
                return new Promise(resolve => {
                    window.setTimeout(() => {
                        resolve(this.$el.scrollLeft);
                    }, 100);
                });
            },
            isSwipeFinished: async function () {
                let positionInitial = 0;
                let positionCurrent = -1;
                while (this.swipeActive) {
                    positionInitial = this.$el.scrollLeft;
                    positionCurrent = await this.getFuturePosition();
                    if (positionInitial === positionCurrent) {
                        this.swipeActive = false;
                    }
                }
                return Promise.resolve(true);
            },
            handleTouchStart() {
                this.swipeActive = true;
                this.$emit('swipeStart');
            },
            handleTouchEnd() {
                this.isSwipeFinished().then(() => {
                    this.$emit('swipeEnd');
                });
            },
            scrollTo(pos) {
                if (this.scrollToMethodAvailable) {
                    this.$refs.slider.scrollTo({
                        left: pos,
                        behavior: 'smooth'
                    });
                } else {
                    this.$refs.slider.scrollLeft = pos;
                }
            },
            scrollForward() {
                const newPosition = Math.round(this.$refs.slider.scrollLeft + this.itemsPerSlide * this.tileWidth);
                if (newPosition <= this.scrollableLength) {
                    this.scrollTo(newPosition);
                } else if (newPosition < Math.round(this.columnsTotal * this.tileWidth)) {
                    this.scrollTo(this.scrollableLength);
                } else {
                    this.scrollTo(0);
                }
            },
            scrollBackward() {
                const newPosition = this.$refs.slider.scrollLeft - this.itemsPerSlide * this.tileWidth;
                if (newPosition >= 0) {
                    this.scrollTo(newPosition);
                } else if (newPosition > -this.itemsPerSlide * this.tileWidth) {
                    this.scrollTo(0);
                } else {
                    this.scrollTo(this.scrollableLength);
                }
            },
            calcTileWidth() {
                const tileStyle = window.getComputedStyle(this.$refs.slider.firstChild);
                this.tileWidth = parseFloat(tileStyle.width) + parseFloat(tileStyle.marginLeft) + parseFloat(tileStyle.marginRight);
            },
            calcIndicatorsWidth() {
                const indicatorsStyle = window.getComputedStyle(this.$refs.indicators.firstChild);
                this.indicatorsWidth = parseInt(indicatorsStyle.width) + parseInt(indicatorsStyle.marginLeft) + parseInt(indicatorsStyle.marginRight);
            },
            indicatorClick(index) {
                this.scrollTo(index * this.itemsPerSlide * this.tileWidth);
            },
            isIndicatorActive(index) {
                const pos = index * this.itemsPerSlide * this.tileWidth;
                const half = this.itemsPerSlide * this.tileWidth / 2;

                if (this.scrollPosition >= pos - half && this.scrollPosition < pos + half) {
                    return true;
                }
                return false;
            },
            handleScroll() {
                this.scrollPosition = this.$refs.slider.scrollLeft;
            },
            handleResize() {
                this.windowWidth = window.innerWidth;
                this.calcTileWidth();
            },
            onScroll() {
                this.$emit('scroll');
            }
        },
        watch: {
            scrollPosition(pos) {
                if (this.visibleIndicators) {
                    const itemsLength = this.indicatorsNumber;
                    const current = Math.ceil((pos / this.tileWidth + 1) / this.itemsPerSlide);
                    const freeze = Math.ceil(this.visibleIndicators / 2);

                    if (itemsLength > this.visibleIndicators) {
                        this.indicatorsDisplacement = current > freeze ?
                            itemsLength <= current + freeze ? (itemsLength - this.visibleIndicators) * -this.indicatorsWidth :
                                (current - freeze) * -this.indicatorsWidth : 0;
                    }
                }
            }
        },
        mounted() {
            this.$nextTick(() => {
                this.handleScroll = throttle(this.handleScroll.bind(this), 100);
                this.handleResize = debounce(this.handleResize.bind(this), 50);

                this.$el.addEventListener('touchstart', this.handleTouchStart, listenersConfig);
                this.$el.addEventListener('touchend', this.handleTouchEnd, listenersConfig);
                this.$refs.slider.addEventListener('scroll', this.handleScroll, listenersConfig);
                window.addEventListener('resize', this.handleResize, { passive: true });
                this.calcTileWidth();
                if (this.visibleIndicators) {
                    this.calcIndicatorsWidth();
                }
            });
        },
        beforeDestroy() {
            this.$el.removeEventListener('touchstart', this.handleTouchStart, listenersConfig);
            this.$el.removeEventListener('touchend', this.handleTouchEnd, listenersConfig);
            this.$refs.slider.removeEventListener('scroll', this.handleScroll, listenersConfig);
            window.removeEventListener('resize', this.handleResize, { passive: true });
        }
    };
</script>
